/**
 * FAQ content — the single source for the visible /faq page AND the FAQPage
 * JSON-LD, which the server renderer (api/render.js) now prints into the
 * served HTML for / and /faq (2026-10-17). index.html no longer carries a
 * hand-copied schema, so the two can't drift.
 *
 * Prices, acre rates and gallery days come from LIVE pricing (`facts`, the
 * /api/pricing/all payload); the bundled fallback catalogue is the default.
 *
 * Google requires the schema to match visible page content, so every answer
 * here renders on /faq as well.
 *
 * Deliberately concrete: "packages start at $150" gets cited; "competitive
 * pricing" does not. Keep answers factual, self-contained, and free of claims
 * that aren't currently true (notably: no drone capability claims beyond what
 * the site already states). Thomas approved the land package's aerial photos
 * (2026-10-11); never mention certifications or insurance anywhere.
 */

import { getDefaultPackages, getDefaultAddOns, LAND_ACREAGE } from '../utils/pricing';

const money = (n) => `$${Number(n).toLocaleString('en-US')}`;

/**
 * Where travel is free, as a phrase ("inside Utah County"), from the live
 * travel settings (/api/pricing/all → travel). null when travel is never charged.
 */
export function freeAreaPhrase(travel) {
  if (travel && travel.enabled === false) return null;
  const area = (travel && travel.freeArea) || 'Utah County';
  if (/^within\b/i.test(area)) return area;
  return /county/i.test(area) ? `inside ${area}` : `in ${area}`;
}

/** One sentence on travel for the FAQ, /locations and llms.txt. */
export function travelSentence(travel) {
  const free = freeAreaPhrase(travel);
  if (!free) return 'There is no travel fee.';
  return `Travel is free ${free}; beyond it, a distance-based travel fee applies, calculated from the property address and shown before you book.`;
}

/**
 * Build the FAQ from live facts: { packages, addOns, landAcreage,
 * mediaExpiryDays, mediaExtension, travel }. Anything missing falls back to
 * the bundled catalogue.
 */
export function buildFaq(facts = {}) {
  const packages = Array.isArray(facts.packages) && facts.packages.length ? facts.packages : getDefaultPackages();
  const addOns = Array.isArray(facts.addOns) && facts.addOns.length ? facts.addOns : getDefaultAddOns();
  const land = facts.landAcreage || LAND_ACREAGE;
  const days = Number(facts.mediaExpiryDays) > 0 ? Number(facts.mediaExpiryDays) : 45;
  const ext = facts.mediaExtension || null;
  const pkg = (id) => packages.find(p => p && (p._id === id || p.id === id)) || {};
  const addOn = (id) => addOns.find(a => a && a.id === id) || {};
  const extensionLine = ext && ext.active === false
    ? 'Download the files before then.'
    : 'You get an email reminder before it comes down, and you can buy an extension to keep it online longer.';

  return [
    {
      q: 'How much does real estate photography cost in Utah?',
      a: `Utah Realty Photos publishes fixed package pricing. The Essential package is ${money(pkg('basic').price)} and covers up to 25 HDR photos. Professional is ${money(pkg('standard').price)} with up to 40 photos, 5 drone photos and 24-hour delivery. Premium is ${money(pkg('premium').price)} and adds a property video, virtual staging and a 2D floor plan. Luxury is ${money(pkg('luxury').price)} and adds a Matterport 3D tour and a cinematic video. Land and vacant lots are ${money(pkg('land-lot').price)} for up to ${land.includedAcres} acre plus ${money(land.pricePerAcre)} for each additional acre. Individual services can also be booked on their own.`
    },
    {
      q: 'How fast are real estate photos delivered?',
      a: `Most packages are delivered within 24 hours of the shoot. The Essential package is delivered the next business day. Your gallery stays online for ${days} days. ${extensionLine}`
    },
    {
      q: 'What areas do you serve?',
      a: 'Utah County and the Wasatch Front, including Eagle Mountain, Saratoga Springs, Lehi, American Fork, Pleasant Grove, Orem, Provo, Draper, Salt Lake City, Park City and Ogden. Properties outside the free service area carry a distance-based travel fee that is calculated from the property address and shown before booking is completed.'
    },
    {
      q: 'Is there a travel fee?',
      a: freeAreaPhrase(facts.travel)
        ? `Travel is included for properties ${freeAreaPhrase(facts.travel)}. Beyond it, a per-mile travel fee applies, subject to a minimum and a cap. The exact amount is calculated from the property address and appears in the order summary before you confirm, so there are no surprises on the invoice.`
        : 'No. There is no travel fee anywhere we shoot.'
    },
    {
      q: 'Do you photograph land and vacant lots?',
      a: `Yes. The Land package is ${money(pkg('land-lot').price)} for a parcel up to ${land.includedAcres} acre, and each additional acre, or part of one, adds ${money(land.pricePerAcre)}. It includes aerial photos covering the whole parcel (at least ${land.aerialPhotosIncluded}, plus ${land.aerialPhotosPerAcre} more for each additional acre) with the boundary drawn on three of them, using county parcel maps, so the lines are approximate, and 10 ground photos of the frontage, access, terrain and views. Any size can be booked online: choose Land or lot on the booking page and enter the acreage, and the price updates before you confirm.`
    },
    {
      q: 'Do you offer 3D tours?',
      a: 'Yes. Matterport 3D walkthroughs can be added to any package or booked on their own. They are included in the Luxury package. A 3D tour is particularly effective for vacant listings, out-of-area buyers, and short-term rental properties.'
    },
    {
      q: 'What do I need to do to get the home ready?',
      a: 'Turn on every light, open the blinds, clear countertops and floors, secure pets, and move cars out of the driveway. Those five things make the biggest difference. Anything left out can usually be worked around, but a prepared home photographs noticeably better and shoots faster.'
    },
    {
      q: 'Do new clients get anything on a first shoot?',
      a: `Yes. A first listing includes a free vertical social reel, normally a ${money(addOn('video-30-agent').price)} add-on, and every shoot is backed by a reshoot guarantee: if the gallery is not right, it gets reshot or it is not paid for.`
    },
    {
      q: 'How far in advance should I book?',
      a: 'Two to three days is comfortable, and same-week slots are often available. Booking is done online: pick a package, pick a time, and the slot is confirmed immediately. Larger properties are automatically allotted more time.'
    },
    {
      q: 'Do you offer virtual staging?',
      a: `Yes. Vacant rooms can be digitally furnished for ${money(addOn('virtual-staging-1').price)} per photo, and virtual twilight conversions (${money(addOn('virtual-twilight-1').price)} per photo) turn a daytime exterior into a dusk shot without a second visit. Both can be added to any package.`
    },
    {
      q: 'Can I use the photos anywhere I want?',
      a: `You receive a license to use the images to market the property — MLS, Zillow, social media, print, and your own website. The photographer retains copyright, which is standard in real estate photography. Download your files while the gallery is online (${days} days, and it can be extended).`
    }
  ];
}

/** The FAQ with the bundled catalogue (pages replace it with live facts). */
export const FAQ_ITEMS = buildFaq();

/** FAQPage JSON-LD for the same items. */
export function faqPageSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } }))
  };
}

export default FAQ_ITEMS;
